import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  FileSearch,
  ClipboardCheck,
  CheckCircle,
  Rocket,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  Package,
  Building,
  Calendar,
  Truck,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

const API_BASE = '/api';

// Types
interface TimelineEvent {
  id: string;
  orderId: string;
  eventType: string;
  eventStatus: 'success' | 'error' | 'warning' | 'info' | 'pending';
  eventMessage: string;
  eventDetails?: Record<string, unknown>;
  createdAt: string;
}

interface FlowStep {
  id: string;
  label: string;
  completed: boolean;
}

interface OrderFlowData {
  order: {
    id: string;
    customerName: string;
    customerCode: string;
    poNumber: string;
    orderDate: string;
    deliveryDate?: string;
    currency: string;
    approvalStatus: string;
    deploymentStatus?: string;
    unleashedOrderNumber?: string;
    items?: { id: string; sku: string; itemName: string; quantity: number; unitPrice: number }[];
  };
  currentStep: string;
  timeline: TimelineEvent[];
  steps: FlowStep[];
}

// Flow step icons and colors
const stepConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  received: { icon: Mail, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  parsed: { icon: FileSearch, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  review: { icon: ClipboardCheck, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
  approved: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50' },
  deploying: { icon: Rocket, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  deployed: { icon: CheckCircle2, color: 'text-perfectted-primary', bgColor: 'bg-perfectted-light' },
  failed: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
};

const statusIcons: Record<string, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Clock,
  pending: RefreshCw,
};

const statusColors: Record<string, string> = {
  success: 'bg-green-50 text-green-700 border-green-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  warning: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  pending: 'bg-gray-50 text-gray-700 border-gray-200',
};

export default function OrderFlowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch order flow data
  const { data, isLoading, error, refetch } = useQuery<{ data: OrderFlowData }>({
    queryKey: ['orderFlow', id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/orders/${id}/flow`);
      if (!res.ok) throw new Error('Failed to fetch order flow');
      return res.json();
    },
    refetchInterval: 5000, // Auto-refresh every 5s
  });

  // Deploy mutation
  const deployMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/unleashed/orders/${id}/deploy`, { method: 'POST' });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error?.message || 'Deployment failed');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderFlow', id] });
      refetch();
    },
  });

  // Retry deployment mutation
  const retryMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/unleashed/orders/${id}/retry`, { method: 'POST' });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error?.message || 'Retry failed');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderFlow', id] });
      refetch();
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/orders/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'approved' }),
      });
      if (!res.ok) throw new Error('Failed to approve order');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderFlow', id] });
      refetch();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-perfectted-primary" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Failed to load order</h2>
        <p className="text-gray-500 mt-2">{error?.message || 'Order not found'}</p>
        <Link to="/orders" className="text-perfectted-primary hover:text-perfectted-dark mt-4 inline-block">
          Back to Orders
        </Link>
      </div>
    );
  }

  const { order, currentStep, timeline, steps } = data.data;
  const canApprove = order.approvalStatus === 'pending';
  const canDeploy = order.approvalStatus === 'approved' &&
                    order.deploymentStatus !== 'deployed' &&
                    order.deploymentStatus !== 'failed';
  const canRetry = order.deploymentStatus === 'failed';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/orders')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">Order Flow: {order.poNumber}</h2>
              {order.unleashedOrderNumber && (
                <span className="px-3 py-1 bg-perfectted-light text-perfectted-dark rounded-full text-sm font-medium">
                  Unleashed: {order.unleashedOrderNumber}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1">{order.customerName}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {canApprove && (
            <button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {approveMutation.isPending ? 'Approving...' : 'Approve Order'}
            </button>
          )}
          {canDeploy && (
            <button
              onClick={() => deployMutation.mutate()}
              disabled={deployMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
            >
              <Rocket className="w-4 h-4" />
              {deployMutation.isPending ? 'Deploying...' : 'Deploy to Unleashed'}
            </button>
          )}
          {canRetry && (
            <button
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={clsx('w-4 h-4', retryMutation.isPending && 'animate-spin')} />
              {retryMutation.isPending ? 'Retrying...' : 'Retry Deployment'}
            </button>
          )}
          <Link
            to={`/orders/${id}`}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            View Details
          </Link>
        </div>
      </div>

      {/* Error display */}
      {(deployMutation.error || retryMutation.error) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-800">Deployment Error</h4>
              <p className="text-red-600 mt-1">
                {(deployMutation.error || retryMutation.error)?.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Flow Stepper */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Order Journey</h3>
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const config = stepConfig[step.id] || stepConfig.received;
            const Icon = config.icon;
            const isActive = currentStep === step.id;
            const isFailed = currentStep === 'failed' && step.id === 'deployed';

            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={clsx(
                      'w-12 h-12 rounded-full flex items-center justify-center transition-all',
                      step.completed && !isFailed
                        ? `${config.bgColor} ${config.color}`
                        : isActive
                        ? 'bg-perfectted-light text-perfectted-primary ring-4 ring-perfectted-light'
                        : isFailed
                        ? 'bg-red-50 text-red-600'
                        : 'bg-gray-100 text-gray-400'
                    )}
                  >
                    {isFailed ? <XCircle className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <span
                    className={clsx(
                      'mt-2 text-sm font-medium',
                      step.completed || isActive ? 'text-gray-900' : 'text-gray-400'
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={clsx(
                      'flex-1 h-1 mx-4 rounded',
                      steps[index + 1].completed || currentStep === steps[index + 1].id
                        ? 'bg-perfectted-primary'
                        : 'bg-gray-200'
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Order Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Items</p>
              <p className="font-semibold text-gray-900">{order.items?.length || 0} line items</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Building className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Customer Code</p>
              <p className="font-semibold text-gray-900">{order.customerCode}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Order Date</p>
              <p className="font-semibold text-gray-900">
                {format(new Date(order.orderDate), 'dd MMM yyyy')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Truck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Delivery Date</p>
              <p className="font-semibold text-gray-900">
                {order.deliveryDate ? format(new Date(order.deliveryDate), 'dd MMM yyyy') : 'TBD'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Timeline</h3>
        <div className="space-y-4">
          {timeline.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No events recorded yet</p>
          ) : (
            timeline.map((event, index) => {
              const StatusIcon = statusIcons[event.eventStatus] || Clock;
              return (
                <div key={event.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        statusColors[event.eventStatus]
                      )}
                    >
                      <StatusIcon className="w-4 h-4" />
                    </div>
                    {index < timeline.length - 1 && (
                      <div className="w-0.5 flex-1 bg-gray-200 my-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{event.eventMessage}</span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(event.createdAt), 'dd MMM HH:mm:ss')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {event.eventType.replace(':', ' - ').replace(/_/g, ' ')}
                    </p>
                    {event.eventDetails && Object.keys(event.eventDetails).length > 0 && (
                      <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(event.eventDetails, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
